import React, { useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { Button } from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import Box from './Box'
import { Discount } from '../../../pure-js/types/DiscountTypes'
import { Currency, Client } from '../../../pure-js/types/GrooverTypes'
import { Texts } from '../../../pure-js/libs/Texts'
import { getDiscountName } from '../libs/TextRepository'
import { getGrooverText, formatGrooverText } from '../libs/GrooverTextRepository'
import { formatPrice } from '../utils/activityUtils'
import useAppState from '../hooks/useAppState'

const DISCOUNT_PREVIEW_COUNT = 2

interface RegistrationDiscountDisplayProps {
  matchedDiscounts: Discount[]
  currency: Currency
  client?: Client
}

const RegistrationDiscountDisplay: React.FC<RegistrationDiscountDisplayProps> = ({
  matchedDiscounts,
  currency,
  client
}) => {
  const { state } = useAppState()
  const language = state.user?.language
  const [expanded, setExpanded] = useState(false)
  /** Multi-course per-course rows: collapsed by default */
  const [multiCourseExpandedByIndex, setMultiCourseExpandedByIndex] = useState<Record<number, boolean>>({})

  const filteredDiscounts = useMemo(
    () =>
      matchedDiscounts.filter((discount) => {
        return discount?.value !== 0 || discount?.type === 'multi_course_matrix' || discount?.type === 'combo'
      }),
    [matchedDiscounts]
  )

  const discountsToRender = expanded ? filteredDiscounts : filteredDiscounts.slice(0, DISCOUNT_PREVIEW_COUNT)
  const hiddenCount = Math.max(0, filteredDiscounts.length - DISCOUNT_PREVIEW_COUNT)

  // Function to format the discount value based on calculation type
  const formatDiscountValue = (discount: Discount): string => {
    switch (discount.calculationType) {
      case 'percentage':
        return `${discount.value}%`
      case 'amount':
        return formatPrice(discount.value, currency)
      default:
        return `${discount.value}` // Fallback in case there's an unknown type
    }
  }
  return (
    <>
      {discountsToRender.map((discount, index) => {
        const isMultiCourseList = discount?.type === 'multi_course_matrix' && !!discount.courseDiscounts?.length
        const isMultiCourseExpanded = multiCourseExpandedByIndex[index] === true
        const showMultiCourseRows = isMultiCourseList && isMultiCourseExpanded
        const hasComboCourseList = discount?.type === 'combo' && !!(discount as any).coursesDiscounts?.length
        const hasVisibleCourseListBelow = !!showMultiCourseRows || hasComboCourseList

        const toggleMultiCourse = () => {
          setMultiCourseExpandedByIndex((prev) => ({
            ...prev,
            [index]: !isMultiCourseExpanded
          }))
        }

        const multiCourseHeaderKeyDown = (e: React.KeyboardEvent) => {
          if (!isMultiCourseList) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggleMultiCourse()
          }
        }

        return (
          <React.Fragment key={`${index}-matched-discounts`}>
            <DiscountTextContainer
              fullWidth
              justify="space-between"
              direction="row"
              hasCourseDiscounts={hasVisibleCourseListBelow}
              $clickable={isMultiCourseList}
              onClick={isMultiCourseList ? toggleMultiCourse : undefined}
              onKeyDown={isMultiCourseList ? multiCourseHeaderKeyDown : undefined}
              role={isMultiCourseList ? 'button' : undefined}
              tabIndex={isMultiCourseList ? 0 : undefined}
              aria-expanded={isMultiCourseList ? isMultiCourseExpanded : undefined}
              aria-label={
                isMultiCourseList
                  ? getGrooverText(
                      isMultiCourseExpanded
                        ? 'userWeb.activityDetail.multiCourseDiscountCollapseDetails'
                        : 'userWeb.activityDetail.multiCourseDiscountExpandDetails',
                      language
                    )
                  : undefined
              }
            >
              <Box>{getDiscountName(discount?.type, client?.id)}</Box>
              {/* For rule-based discounts we usually don't show a single top-level value */}
              {discount.type !== 'multi_course_matrix' && discount.type !== 'combo' && (
                <Box>
                  {[`NaN ${currency}`, 'undefined%'].includes(formatDiscountValue(discount))
                    ? null
                    : formatDiscountValue(discount)}
                </Box>
              )}
              {isMultiCourseList && (
                <ExpandMore
                  sx={{
                    color: '#0c9f2c',
                    flexShrink: 0,
                    transform: isMultiCourseExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                  aria-hidden
                />
              )}
            </DiscountTextContainer>

            {/* Multi-course matrix per-course rules */}
            {discount.type === 'multi_course_matrix' && discount.courseDiscounts && showMultiCourseRows && (
              <CourseDiscountList>
                {discount.courseDiscounts.map((courseDiscount, i) => (
                  <CourseDiscountItem key={`${i}-discount-index`}>
                    <Box direction="row">
                      {formatGrooverText(
                        getGrooverText(Texts.componentsPublicDiscountCssPublicActivityDiscountSubRule),
                        [`${courseDiscount.course}`]
                      )}
                    </Box>
                    <Box>
                      {discount.calculationType === 'percentage'
                        ? `${courseDiscount.discount}%`
                        : formatPrice(courseDiscount.discount, currency)}
                    </Box>
                  </CourseDiscountItem>
                ))}
              </CourseDiscountList>
            )}

            {/* Combo discount rules (show list of course id combos) */}
            {discount.type === 'combo' && (discount as any).coursesDiscounts && (
              <CourseDiscountList>
                {(discount as any).coursesDiscounts
                  .filter((combo: { discount: number }) => combo.discount > 0)
                  .map((combo: { name?: string; courses: string[]; discount: number }, i: number) => (
                    <CourseDiscountItem key={`${i}-combo-discount-index`}>
                      <Box direction="row">{combo.name ?? combo.courses.join(' + ')}</Box>
                      <Box>{isNaN(combo.discount) ? null : formatPrice(combo.discount, currency)}</Box>
                    </CourseDiscountItem>
                  ))}
              </CourseDiscountList>
            )}
          </React.Fragment>
        )
      })}
      {hiddenCount > 0 && (
        <ExpandDiscountsBox fullWidth>
          <Button
            variant="text"
            size="small"
            onClick={() => setExpanded((e) => !e)}
            sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
          >
            {expanded
              ? getGrooverText('userWeb.activityDetail.discountsShowLess', language)
              : hiddenCount === 1
                ? getGrooverText('userWeb.activityDetail.discountsShowMoreSingular', language)
                : formatGrooverText(getGrooverText('userWeb.activityDetail.discountsShowMore', language), [
                    String(hiddenCount)
                  ])}
          </Button>
        </ExpandDiscountsBox>
      )}
    </>
  )
}

const ExpandDiscountsBox = styled(Box)`
  margin-top: 4px;
  width: 100%;
`

// Conditionally apply margin-bottom based on the presence of course discounts
const DiscountTextContainer = styled(Box)<{ hasCourseDiscounts: boolean; $clickable?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-size: 16px;
  border-radius: 4px;
  color: #0c9f2c;
  background-color: rgba(0, 119, 24, 0.05);
  padding: 8px;
  border-left: 4px solid #0c9f2c;
  box-sizing: border-box; /* Ensure padding is included in the element's width */
  margin-bottom: ${({ hasCourseDiscounts }) => (hasCourseDiscounts ? '0' : '16px')}; /* Remove margin when needed */
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  gap: 8px;
  &:focus-visible {
    outline: 2px solid #0c9f2c;
    outline-offset: 2px;
  }
`

const CourseDiscountList = styled(Box)`
  color: #666;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: rgba(0, 119, 24, 0.1);
  border-left: 4px solid #0c9f2c;
  padding-left: 20px;
  padding-bottom: 8px;
  padding-top: 8px;
  margin-bottom: 16px;
`

const CourseDiscountItem = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-size: 16px;

  align-items: center;
  width: 100%;
  padding-right: 8px;
`

export default RegistrationDiscountDisplay
